import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { Droplets, Pill, Calendar as CalIcon, FlaskConical, Sparkles, ArrowRight, AlertTriangle, CheckCircle2, Syringe } from "lucide-react";
import { toast } from "sonner";
import PushPrompt from "@/components/PushPrompt";

export default function DashboardPage() {
  const { user } = useAuth();
  const [activeProtocol, setActiveProtocol] = useState(null);
  const [evolution, setEvolution] = useState({ overall_score: 0, series: [], checkins_count: 0 });
  const [appointments, setAppointments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [todayChecklist, setTodayChecklist] = useState({});
  const [disabledByAdmin, setDisabledByAdmin] = useState(false);

  const loadAll = async () => {
    try {
      const [ap, ev, apps, applcs, cl] = await Promise.all([
        api.get("/protocols/me/active"),
        api.get("/score/evolution"),
        api.get("/appointments"),
        api.get("/applications"),
        api.get("/checklist/daily"),
      ]);
      setActiveProtocol(ap.data);
      setDisabledByAdmin(!!ap.data?.disabled_by_admin);
      setEvolution(ev.data);
      setAppointments(apps.data);
      setApplications(applcs.data);
      setTodayChecklist(cl.data || {});
    } catch (_) { /* noop */ }
  };

  useEffect(() => {
    loadAll();
    // Auto-refresh every 20s so admin changes reflect without manual reload
    const t = setInterval(loadAll, 20000);
    return () => clearInterval(t);
  }, []);

  const requestAiSummary = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/evolution-summary");
      setAiSummary(data);
    } catch (e) {
      toast.error("Falha ao gerar resumo clínico");
    } finally {
      setAiLoading(false);
    }
  };

  const currentPhase = activeProtocol?.current_phase;
  const protocol = activeProtocol?.protocol;
  const progressPct = protocol ? Math.round((activeProtocol.current_day / protocol.duration_days) * 100) : 0;
  const nextAppointment = appointments.filter((a) => a.status === "scheduled")[0];
  const nextApplication = applications.filter((a) => a.status === "scheduled")[0];
  const checklistDone = !!(todayChecklist.water && todayChecklist.evacuation && todayChecklist.supplementation && todayChecklist.food);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <PushPrompt />

      {disabledByAdmin && (
        <div className="rounded-sm border border-warning/40 bg-warning/10 p-4" data-testid="protocol-disabled-banner">
          <div className="font-semibold text-foreground">Protocolo temporariamente pausado</div>
          <div className="text-sm text-muted-foreground mt-1">Sua equipe clínica pausou seu protocolo. Entre em contato pelo chat para saber quando será reativado.</div>
        </div>
      )}

      {/* Booking CTA — attached to the patient panel */}
      <Link
        to="/app/agendamentos"
        state={{ openBooking: true }}
        className="block bg-primary text-primary-foreground rounded-sm p-6 hover:bg-primary/90 transition-colors group"
        data-testid="dashboard-booking-cta"
      >
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 rounded-sm bg-gold/20 text-gold grid place-items-center flex-none">
            <Syringe className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline text-gold mb-1">Agendar serviço</div>
            <div className="brand-wordmark uppercase font-bold text-xl">Marque sua próxima sessão</div>
            <div className="text-sm opacity-80 mt-1">Terapias injetáveis, soroterapia, consultas farmacêuticas e integrativas.</div>
          </div>
          <ArrowRight className="w-6 h-6 text-gold group-hover:translate-x-1 transition-transform" />
        </div>
      </Link>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="overline text-accent mb-3">Painel do paciente</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
            Olá, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-muted-foreground mt-2">
            {protocol ? `${protocol.name} — dia ${activeProtocol.current_day} de ${protocol.duration_days}` : "Você ainda não iniciou um protocolo."}
          </p>
        </div>
        <div className="bg-card border border-border rounded-sm p-5 min-w-[260px]">
          <div className="overline text-muted-foreground mb-2">Score geral de evolução</div>
          <div className="flex items-end gap-2">
            <div className="font-heading text-4xl font-bold text-primary" data-testid="dashboard-score">{evolution.overall_score}%</div>
            <div className="text-xs text-muted-foreground mb-1.5">{evolution.checkins_count} check-ins</div>
          </div>
          <div className="mt-3 h-1.5 w-full bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(evolution.overall_score, 100)}%` }} />
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Protocol */}
        <div className="bg-card border border-border rounded-sm p-6 lg:col-span-2" data-testid="card-current-protocol">
          <div className="flex items-center justify-between mb-4">
            <div className="overline text-muted-foreground">Protocolo atual</div>
            {protocol && <div className="overline text-success">Ativo</div>}
          </div>
          {protocol ? (
            <>
              <h3 className="font-heading text-xl font-semibold">{protocol.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">{currentPhase?.name}</p>
              <div className="mt-5 h-2 w-full bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
              </div>
              <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                <span>Dia {activeProtocol.current_day}</span>
                <span>{progressPct}% concluído</span>
                <span>{protocol.duration_days} dias</span>
              </div>
              {currentPhase && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="overline text-muted-foreground mb-2">Objetivos da fase</div>
                    <ul className="text-sm space-y-1.5">
                      {currentPhase.objectives.map((o) => (
                        <li key={o} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-accent flex-none mt-0.5" /> {o}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="overline text-muted-foreground mb-2">Suplementação</div>
                    <ul className="text-sm space-y-1.5 text-muted-foreground">
                      {currentPhase.supplementation.map((s) => <li key={s}>• {s}</li>)}
                    </ul>
                  </div>
                </div>
              )}
              <Link to="/app/protocolos" className="inline-flex items-center gap-2 mt-6 text-primary text-sm font-semibold hover:underline" data-testid="continue-protocol-link">
                Continuar protocolo <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Escolha um dos protocolos clínicos disponíveis para iniciar seu acompanhamento.</p>
              <Link to="/app/protocolos" className="inline-flex items-center gap-2 mt-4 text-primary text-sm font-semibold hover:underline" data-testid="choose-protocol-link">
                Escolher protocolo <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </div>

        {/* Next task */}
        <div className="bg-card border border-border rounded-sm p-6" data-testid="card-next-task">
          <div className="overline text-muted-foreground mb-4">Próxima tarefa</div>
          {!checklistDone ? (
            <>
              <Droplets className="w-6 h-6 text-accent mb-3" />
              <h3 className="font-heading text-lg font-semibold">Checklist diário</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Água, evacuação, suplementação e alimentação de hoje.
              </p>
              <Link to="/app/protocolos" className="inline-flex items-center gap-2 mt-4 text-primary text-sm font-semibold hover:underline" data-testid="task-checklist-link">
                Marcar agora <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          ) : (
            <>
              <Sparkles className="w-6 h-6 text-success mb-3" />
              <h3 className="font-heading text-lg font-semibold">Check-in semanal</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Checklist de hoje concluído. Que tal fazer o check-in da semana?
              </p>
              <Link to="/app/check-in" className="inline-flex items-center gap-2 mt-4 text-primary text-sm font-semibold hover:underline" data-testid="task-checkin-link">
                Fazer check-in <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </div>

        {/* Evolution chart */}
        <div className="bg-card border border-border rounded-sm p-6 lg:col-span-2" data-testid="card-evolution">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline text-muted-foreground">Evolução clínica</div>
              <h3 className="font-heading text-lg font-semibold mt-1">Sintomas por check-in</h3>
            </div>
          </div>
          <div className="h-64">
            {evolution.series && evolution.series.length > 0 ? (
              <ResponsiveContainer>
                <LineChart data={evolution.series} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis domain={[0, 10]} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="energy" name="Energia" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="sleep" name="Sono" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="intestine" name="Intestino" stroke="hsl(var(--chart-3))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="adherence" name="Adesão" stroke="hsl(var(--chart-4))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">
                Realize seu primeiro check-in para visualizar a evolução.
              </div>
            )}
          </div>
        </div>

        {/* Appointments */}
        <div className="bg-card border border-border rounded-sm p-6" data-testid="card-next-appointments">
          <div className="overline text-muted-foreground mb-4">Próximas consultas</div>
          {nextAppointment ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CalIcon className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <div className="font-semibold text-sm capitalize">{nextAppointment.appointment_type}</div>
                  <div className="text-xs text-muted-foreground">{new Date(nextAppointment.scheduled_at).toLocaleString("pt-BR")}</div>
                  <div className="text-xs text-muted-foreground mt-1">{nextAppointment.professional}</div>
                </div>
              </div>
              <Link to="/app/agendamentos" className="inline-flex items-center gap-2 text-primary text-sm font-semibold hover:underline" data-testid="appointments-link">
                Ver todas <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Nenhuma consulta agendada.</p>
              <Link to="/app/agendamentos" className="inline-flex items-center gap-2 mt-3 text-primary text-sm font-semibold hover:underline" data-testid="appointments-empty-link">
                Agendar consulta <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </div>

        {/* AI Summary */}
        <div className="bg-primary text-primary-foreground rounded-sm p-6 lg:col-span-3" data-testid="card-ai-summary">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="overline opacity-80 mb-3 flex items-center gap-2"><Sparkles className="w-3 h-3" /> Resumo clínico por IA</div>
              <h3 className="font-heading text-xl font-semibold">Análise farmacêutica assistida</h3>
              <p className="text-sm opacity-80 mt-1 max-w-2xl">
                Claude Sonnet 4.5 analisa seus últimos check-ins e gera um resumo clínico objetivo para apoiar seu acompanhamento.
              </p>
            </div>
            <button
              onClick={requestAiSummary}
              disabled={aiLoading}
              className="rounded-sm bg-primary-foreground text-primary px-4 py-2.5 text-sm font-semibold hover:bg-primary-foreground/90 disabled:opacity-60 transition-colors"
              data-testid="ai-generate-btn"
            >
              {aiLoading ? "Analisando…" : "Gerar resumo"}
            </button>
          </div>
          {aiSummary && (
            <div className="mt-6 bg-primary-foreground/10 border border-primary-foreground/20 rounded-sm p-4" data-testid="ai-summary-result">
              {aiSummary.alert && (
                <div className="flex items-center gap-2 text-xs mb-2 opacity-90">
                  <AlertTriangle className="w-4 h-4" /> Alerta: possível piora de sintomas detectada.
                </div>
              )}
              <p className="text-sm leading-relaxed">{aiSummary.summary}</p>
              <div className="text-xs opacity-70 mt-3">Gerado em {new Date(aiSummary.generated_at).toLocaleString("pt-BR")}</div>
            </div>
          )}
        </div>

        {/* Applications table (read-only, admin-managed) */}
        <div className="bg-card border border-border rounded-sm p-6 lg:col-span-3" data-testid="card-applications-table">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="overline text-muted-foreground">Aplicações injetáveis</div>
              <h3 className="font-heading text-lg font-semibold mt-1">Controle de aplicações</h3>
              <p className="text-xs text-muted-foreground mt-1">Gerenciado pela sua equipe clínica. Atualiza automaticamente a cada 20s.</p>
            </div>
          </div>
          {applications.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Nenhuma aplicação registrada ainda.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left">
                  <tr>
                    <th className="py-2 overline text-muted-foreground">Aplicação</th>
                    <th className="py-2 overline text-muted-foreground">Data/Hora</th>
                    <th className="py-2 overline text-muted-foreground">Profissional</th>
                    <th className="py-2 overline text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((a) => {
                    const statusCls = a.status === "done"
                      ? "border-success/40 text-success bg-success/5"
                      : a.status === "cancelled"
                        ? "border-destructive/40 text-destructive bg-destructive/5"
                        : "border-primary/40 text-primary bg-primary/5";
                    const statusLbl = a.status === "done" ? "Realizada" : a.status === "cancelled" ? "Cancelada" : "Agendada";
                    return (
                      <tr key={a.id} className="border-b border-border last:border-0" data-testid={`patient-application-row-${a.id}`}>
                        <td className="py-3">
                          <div className="font-medium inline-flex items-center gap-2"><FlaskConical className="w-4 h-4 text-accent" /> {a.substance}</div>
                          <div className="text-xs text-muted-foreground">{a.dose} · {a.via}{a.location ? ` · ${a.location}` : ""}</div>
                        </td>
                        <td className="py-3 text-muted-foreground">{new Date(a.scheduled_at).toLocaleString("pt-BR")}</td>
                        <td className="py-3 text-muted-foreground text-xs">{a.professional_name || "—"}</td>
                        <td className="py-3">
                          <span className={`inline-block overline px-2 py-0.5 rounded-sm border ${statusCls}`}>{statusLbl}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
